import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  HostBinding,
  signal,
  computed,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { TableColumn, FilterChip } from '../data-table/data-table.component';
import { TablePreferencesService } from '@shared/services/table-preferences.service';

export interface FilterMenuOption {
  key: string;
  label: string;
  value: any;
  active?: boolean;
}

export interface FilterMenuGroup {
  key: string;
  label: string;
  options: FilterMenuOption[];
}

export interface FilterMenuSelection {
  key: string;
  value: any;
}

export interface ToolbarSortSelection {
  sortBy: string;
  direction: 'asc' | 'desc';
}

export interface ToolbarPrimaryActionOption {
  label: string;
  icon?: string;
  value: string;
}

export interface ToolbarViewOption {
  value: string;
  icon: string;
  label: string;
}

@Component({
  selector: 'app-table-toolbar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('searchExpand', [
      state('collapsed', style({ width: '0', opacity: 0, padding: '0' })),
      state('expanded', style({ width: '220px', opacity: 1 })),
      transition('collapsed <=> expanded', animate('200ms ease-in-out'))
    ])
  ],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatMenuModule,
    MatCheckboxModule,
    MatChipsModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './table-toolbar.component.html',
  styleUrls: ['./table-toolbar.component.scss']
})
export class TableToolbarComponent implements OnChanges, OnDestroy {
  @HostBinding('attr.title') readonly hostTitle = null;

  @Input() columns: TableColumn[] = [];
  @Input() activeFilters: FilterChip[] = [];
  @Input() density: 'compact' | 'normal' | 'comfortable' = 'normal';
  @Input() tableId?: string;
  @Input() title?: string;
  @Input() titleIcon?: string;
  @Input() titleAriaLabel?: string;
  @Input() titleInfoTooltip?: string;
  @Input() enableSearch = false;
  @Input() searchPlaceholder = 'Buscar...';
  @Input() isLoading = false;
  @Input() enableColumnVisibility = false;
  @Input() enableDensity = false;
  @Input() enableExport = false;
  @Input() enableFilter = false;
  @Input() enableClearFilters = false;
  @Input() enableSort = false;
  @Input() sortState?: ToolbarSortSelection | null;
  @Input() sortHiddenColumnKeys: string[] = [];
  @Input() filterMenuTitle = 'Filtros';
  @Input() filterOptions: FilterMenuOption[] = [];
  @Input() filterGroups: FilterMenuGroup[] = [];
  @Input() primaryActionLabel?: string;
  @Input() primaryActionMobileLabel?: string;
  @Input() primaryActionOptions: ToolbarPrimaryActionOption[] = [];
  @Input() viewOptions: ToolbarViewOption[] = [];
  @Input() activeView = 'table';
  @Input('readonly') readOnly = false;

  @Output() searchChange = new EventEmitter<string>();
  @Output() searchInput = new EventEmitter<string>();
  @Output() filterRemove = new EventEmitter<string>();
  @Output() filterOptionSelect = new EventEmitter<FilterMenuSelection>();
  @Output() clearFiltersClick = new EventEmitter<void>();
  @Output() sortChange = new EventEmitter<ToolbarSortSelection>();
  @Output() columnVisibilityChange = new EventEmitter<{ key: string; visible: boolean }>();
  @Output() densityChange = new EventEmitter<'compact' | 'normal' | 'comfortable'>();
  @Output() exportClick = new EventEmitter<void>();
  @Output() primaryActionClick = new EventEmitter<void>();
  @Output() primaryActionOptionClick = new EventEmitter<ToolbarPrimaryActionOption>();
  @Output() viewChange = new EventEmitter<string>();
  @Output() titleInfoClick = new EventEmitter<void>();

  searchOpen = false;
  searchValue = '';
  currentDensity: 'compact' | 'normal' | 'comfortable' = 'normal';
  filterPanelOpen = false;

  @ViewChild('searchInputEl') private searchInputEl?: ElementRef<HTMLInputElement>;

  private searchInput$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  readonly densityOptions: { value: 'compact' | 'normal' | 'comfortable'; icon: string; label: string }[] = [
    { value: 'compact', icon: 'density_small', label: 'Compacto' },
    { value: 'normal', icon: 'density_medium', label: 'Normal' },
    { value: 'comfortable', icon: 'density_large', label: 'Cómodo' }
  ];

  get hideableColumns(): TableColumn[] {
    return this.columns.filter(c => c.hideable);
  }

  get hasActiveSearch(): boolean {
    return this.searchValue.trim().length > 0;
  }

  get visibleFilterCount(): number {
    return this.activeFilters.length + (this.hasActiveSearch ? 1 : 0);
  }

  get hasFilterOptions(): boolean {
    return this.filterOptions.length > 0 || this.filterGroups.length > 0;
  }

  get sortableColumns(): TableColumn[] {
    return this.columns.filter(c => c.sortable && !this.sortHiddenColumnKeys.includes(c.key));
  }

  get hasActiveSort(): boolean {
    return !!this.sortState?.sortBy;
  }

  get filterMenuGroups(): FilterMenuGroup[] {
    if (this.filterGroups.length > 0) {
      return this.filterGroups;
    }

    return this.filterOptions.length > 0
      ? [{ key: 'default', label: this.filterMenuTitle, options: this.filterOptions }]
      : [];
  }

  trackFilterGroup(_index: number, group: FilterMenuGroup): string {
    return group.key;
  }

  trackColumn(_index: number, column: TableColumn): string {
    return column.key;
  }

  get canShowPrimaryAction(): boolean {
    return !this.readOnly && !!this.primaryActionLabel;
  }

  get activeViewOption(): ToolbarViewOption | undefined {
    return this.viewOptions.find(option => option.value === this.activeView) ?? this.viewOptions[0];
  }

  get primaryActionMobileText(): string {
    return this.primaryActionMobileLabel || '+';
  }

  constructor(
    private tablePrefs: TablePreferencesService,
    private cdr: ChangeDetectorRef
  ) {
    this.searchInput$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(term => this.searchChange.emit(term));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['density']) {
      this.currentDensity = this.density;
    }
    if ((changes['tableId'] || changes['columns']) && this.tableId) {
      const prefs = this.tablePrefs.load(this.tableId);
      if (prefs.density) {
        this.currentDensity = prefs.density;
      }
      if (prefs.visibleColumns) {
        this.columns.forEach(column => {
          column.visible = prefs.visibleColumns!.includes(column.key);
        });
      }
      if ((changes['tableId'] || changes['columns']) && !this.sortState && prefs.sort &&
          this.sortableColumns.some(column => column.key === prefs.sort!.sortBy)) {
        this.sortChange.emit(prefs.sort);
      }
    }
    if ((changes['tableId'] || changes['viewOptions']?.firstChange) && this.tableId && this.viewOptions.length > 1) {
      const savedView = this.tablePrefs.load(this.tableId).view;
      if (savedView && this.viewOptions.some(option => option.value === savedView)) {
        this.activeView = savedView;
        this.viewChange.emit(savedView);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleSearch(): void {
    if (this.searchOpen && this.searchValue) {
      this.clearSearch();
    } else {
      this.searchOpen = !this.searchOpen;
      if (this.searchOpen) {
        this.focusSearchInput();
      }
    }
    this.cdr.markForCheck();
  }

  onSearchInput(value: string): void {
    this.searchValue = value;
    this.searchInput.emit(value);
    this.searchInput$.next(value);
    this.cdr.markForCheck();
  }

  clearSearch(): void {
    this.searchValue = '';
    this.searchOpen = false;
    this.searchChange.emit('');
    this.cdr.markForCheck();
  }

  removeFilter(key: string): void {
    this.filterRemove.emit(key);
  }

  selectFilterOption(option: FilterMenuOption, _event?: Event): void {
    this.filterOptionSelect.emit({ key: option.key, value: option.value });
  }

  selectSortColumn(column: TableColumn): void {
    if (!column.sortable) {
      return;
    }

    const direction: 'asc' | 'desc' = this.sortState?.sortBy === column.key && this.sortState.direction === 'asc'
      ? 'desc'
      : 'asc';

    const selection = { sortBy: column.key, direction };
    if (this.tableId) {
      this.tablePrefs.patch(this.tableId, { sort: selection });
    }
    this.sortChange.emit(selection);
  }

  getSortIcon(column: TableColumn): string {
    if (this.sortState?.sortBy !== column.key) {
      return '';
    }

    return this.sortState.direction === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  toggleColumn(key: string, visible: boolean): void {
    const col = this.columns.find(c => c.key === key);
    if (col) col.visible = visible;
    this.columnVisibilityChange.emit({ key, visible });
  }

  isColumnVisible(key: string): boolean {
    return this.columns.find(c => c.key === key)?.visible !== false;
  }

  readonly densityLabels: Record<'compact' | 'normal' | 'comfortable', string> = {
    compact: 'Compacto',
    normal: 'Normal',
    comfortable: 'Cómodo'
  };

  cycleDensity(): void {
    const order: ('compact' | 'normal' | 'comfortable')[] = ['compact', 'normal', 'comfortable'];
    const next = order[(order.indexOf(this.currentDensity) + 1) % order.length];
    this.setDensity(next);
  }

  setDensity(density: 'compact' | 'normal' | 'comfortable'): void {
    this.currentDensity = density;
    this.densityChange.emit(density);
    this.cdr.markForCheck();
  }

  onExport(): void {
    this.exportClick.emit();
  }

  onPrimaryAction(): void {
    if (this.readOnly) {
      return;
    }

    this.primaryActionClick.emit();
  }

  onPrimaryActionOption(option: ToolbarPrimaryActionOption): void {
    if (this.readOnly) {
      return;
    }

    this.primaryActionOptionClick.emit(option);
  }

  selectView(value: string): void {
    if (this.activeView === value) {
      return;
    }

    this.activeView = value;
    if (this.tableId) {
      this.tablePrefs.patch(this.tableId, { view: value });
    }
    this.viewChange.emit(value);
    this.cdr.markForCheck();
  }

  toggleFilterPanel(): void {
    this.filterPanelOpen = !this.filterPanelOpen;
    this.cdr.markForCheck();
  }

  private focusSearchInput(): void {
    setTimeout(() => {
      this.searchInputEl?.nativeElement.focus();
      this.searchInputEl?.nativeElement.select();
    }, 220);
  }
}
